import { useCallback, useState, type RefObject } from 'react';
import type { JiraAttachment } from '@shared/types.js';
import { useAddComment } from '@/hooks/useComments.js';
import { useEditorUpload } from '@/hooks/useIssues.js';
import type { RichEditorHandle } from '@/features/editor/RichEditor.js';
import { LazyRichEditor as RichEditor } from '@/features/editor/LazyRichEditor.js';

interface CommentFormProps {
  issueKey: string;
  projectKey?: string;
  attachments?: JiraAttachment[];
  /** Owned by the issue page so Reply buttons can start a comment */
  editorRef: RefObject<RichEditorHandle | null>;
}

export function CommentForm({ issueKey, projectKey, attachments, editorRef }: CommentFormProps) {
  const [canSubmit, setCanSubmit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const addComment = useAddComment(issueKey);
  const upload = useEditorUpload(issueKey);

  const refreshState = useCallback(() => {
    const ed = editorRef.current;
    setCanSubmit(!!ed && !ed.isEmpty());
    setUploading(!!ed?.isUploading());
  }, [editorRef]);

  const handleSubmit = useCallback(() => {
    const ed = editorRef.current;
    if (!ed || ed.isEmpty() || ed.isUploading() || addComment.isPending) return;
    addComment.mutate({ adf: ed.getAdf() }, {
      onSuccess: () => {
        // Fresh editor: clears content, history and any upload state
        setEditorKey((k) => k + 1);
        setCanSubmit(false);
      },
    });
  }, [addComment, editorRef]);

  return (
    <form
      id="comment-form"
      onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
      className="mt-4"
    >
      <RichEditor
        key={editorKey}
        ref={editorRef}
        placeholder="Add a comment… Type @ to mention, paste or drop images and files"
        attachments={attachments}
        projectKey={projectKey}
        onUploadFile={upload}
        onChange={refreshState}
        onSubmit={handleSubmit}
      />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {uploading ? 'Uploading attachments…' : 'Ctrl+Enter to post'}
        </span>
        <button
          type="submit"
          disabled={!canSubmit || uploading || addComment.isPending}
          className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {addComment.isPending ? 'Posting...' : 'Comment'}
        </button>
      </div>
      {addComment.isError && (
        <p className="mt-1 text-sm text-red-600">Failed to post. Please try again.</p>
      )}
    </form>
  );
}
